import { registerSW } from "virtual:pwa-register";
import { logError } from "./hooks/useErrorLogging";

export function initPWA() {
    if (import.meta.env.DEV) {
        // Avoid SW caching in dev (can trap old auth bundles and hang the loader).
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                if (registrations.length === 0) return;

                registrations.forEach((registration) => registration.unregister());

                // Reload once to escape SW-controlled pages.
                try {
                    const key = 'dev-sw-unregistered-reloaded';
                    if (!sessionStorage.getItem(key)) {
                        sessionStorage.setItem(key, '1');
                        window.location.reload();
                    }
                } catch {
                    window.location.reload();
                }
            });
        }
    } else {
        // Register service worker using vite-plugin-pwa
        // This ensures proper SW generation and registration
        registerSW({
            immediate: true,
            onRegisteredSW(swUrl, registration) {
                console.log('[SW] Registered:', swUrl);

                // Log successful registration
                console.log('Service worker registered', {
                    type: 'pwa_diagnostic',
                    event: 'sw_registered',
                    swUrl,
                    scope: registration?.scope,
                });

                // Check for updates periodically (every 15 minutes)
                setInterval(() => {
                    registration?.update().catch((err) => {
                        console.debug('[SW] Background update failed (likely offline):', err);
                    });
                }, 15 * 60 * 1000);
            },
            onRegisterError(error) {
                console.error('[SW] Registration failed:', error);

                // Log registration failure
                logError('Service worker registration failed', {
                    type: 'pwa_diagnostic',
                    event: 'sw_error',
                    errorMessage: error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined,
                });
            },
            onNeedRefresh() {
                // New version available - notify via custom event instead of auto-refresh
                console.log('[SW] New version available, notifying UI...');
                window.dispatchEvent(new CustomEvent('app-update-available', {
                    detail: {
                        updateServiceWorker: async () => {
                            // This function will be called by the UI to trigger the update
                            const registration = await navigator.serviceWorker.getRegistration();
                            if (registration?.waiting) {
                                registration.waiting.postMessage({ type: 'SKIP_WAITING' });

                                // After skipWaiting, the new SW will activate and we can reload
                                sessionStorage.setItem('app-just-updated', 'true');
                                window.location.reload();
                            } else {
                                // Fallback: just reload if something is weird
                                window.location.reload();
                            }
                        }
                    }
                }));
            },
        });
    }
}
